import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './ProjectFive.css';

const STEPS = [
  {
    num: '01',
    label: 'WRITE YOUR REALITY',
    desc: 'Write every category of your future: career, health, money, relationships, whatever matters to you. Break it into categories. Go as deep as you want. Write it as if it already exists.',
  },
  {
    num: '02',
    label: 'DOCUMENT YOUR JOURNEY',
    desc: 'Every decision, every hard day, every win. Add updates to your records as you move. Your entire journey gets recorded in real time, timestamped, permanently yours.',
  },
  {
    num: '03',
    label: 'LET AI NAVIGATE',
    desc: 'Stuck on what to do next? Dmyor Assist reads your documented journey and surfaces concrete next steps. Based on where you are, where you said you would be, and what is falling behind.',
  },
  {
    num: '04',
    label: 'FIND YOUR PEOPLE',
    desc: 'PROJECT-5 is building a community with the same mindset. People who push each other, share what is working, and hold each other accountable over five years.',
  },
  {
    num: '05',
    label: 'RELEASE WHEN YOU ARE DONE',
    desc: 'When you reach the reality you wrote, or decide your chapter is complete, you release your records. Your name goes public. Your story stops being anonymous. You built it.',
  },
];

const FEATURES = [
  {
    num: '01',
    label: 'LIVE COUNTDOWN',
    desc: 'Your dashboard shows exactly how many days remain in your 5-year window. Every day is visible. Nothing lets you pretend time isn\'t moving.',
    tag: 'ALWAYS ON',
  },
  {
    num: '02',
    label: 'DMYOR ASSIST',
    desc: 'AI that reads your full journey and tells you your next concrete step. Not generic advice. Specific to your records, your progress, and your gaps right now.',
    tag: 'AI-POWERED',
  },
  {
    num: '03',
    label: 'SPACE VIEW',
    desc: 'See your entire vision in one place. A cosmic view of all your records, their status, and your momentum across every category of your life.',
    tag: 'VISUAL',
  },
  {
    num: '04',
    label: 'VISIBILITY CONTROL',
    desc: 'Everything stays private by default. You choose what to share and when. Your vision is yours until you decide otherwise.',
    tag: 'YOUR CHOICE',
  },
  {
    num: '05',
    label: 'JOURNEY ANALYSIS',
    desc: 'A full read of where you are versus where you said you would be. What is going well, what is falling behind, and what deserves your focus next.',
    tag: 'COMING SOON',
  },
];

const ProjectFive = () => {
  const canvasRef = useRef(null);
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const dots = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 1.2 + 0.3,
      speed: Math.random() * 0.3 + 0.05,
      color: ['#ffe600', '#ff003c', '#ff6600', '#ff2255'][Math.floor(Math.random() * 4)],
      opacity: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = d.opacity;
        ctx.fill();
        d.y -= d.speed;
        if (d.y < -4) { d.y = canvas.height + 4; d.x = Math.random() * canvas.width; }
      });
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animId); };
  }, []);

  useEffect(() => {
    const scheduleGlitch = () => {
      const delay = 600 + Math.random() * 1200;
      return setTimeout(() => {
        setGlitch(true);
        const duration = 80 + Math.random() * 100;
        setTimeout(() => {
          setGlitch(false);
          if (Math.random() < 0.3) {
            setTimeout(() => {
              setGlitch(true);
              setTimeout(() => setGlitch(false), 60);
            }, 80);
          }
        }, duration);
        t = scheduleGlitch();
      }, delay);
    };
    let t = scheduleGlitch();
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="p5-page">
      <Helmet>
        <title>PROJECT-5 | 5 Years. One Life. No Excuses.</title>
        <link rel="canonical" href="https://www.dmyor.com/project-5" />
        <meta name="description" content="PROJECT-5 is a 5-year commitment to designing the life you want. Write your reality. Document every step. Build it for real." />
      </Helmet>

      <canvas className="p5-canvas" ref={canvasRef} />
      <div className="p5-scanlines" aria-hidden="true" />

      {/* ── HERO ── */}
      <section className="p5-hero">
        <div className="p5-hero-inner">
          <div className="p5-system-tag">DMYOR // PROJECT-5 // 2026</div>

          <h1 className={`p5-title ${glitch ? 'glitch' : ''}`} data-text="PROJECT-5">
            PROJECT-5
          </h1>

          <div className="p5-tagline-row">
            <span className="p5-tag-item p5-green">5 YEARS</span>
            <span className="p5-tag-sep">·</span>
            <span className="p5-tag-item p5-red">ONE LIFE</span>
            <span className="p5-tag-sep">·</span>
            <span className="p5-tag-item p5-cyan">NO EXCUSES</span>
          </div>

          <p className="p5-hero-sub">
            Five years from now, your life will be somewhere. The question is
            whether you decided where, or just let it happen. PROJECT-5 is a
            commitment to design your own future. Write it, build toward it,
            document every step. If you stay with it for five years, you will
            enter the future you built here.
          </p>

          <div className="p5-hero-cta">
            <Link to="/signup" className="p5-btn-primary">
              <span className="p5-btn-bracket">[</span>
              START PROJECT-5
              <span className="p5-btn-bracket">]</span>
            </Link>
            <Link to="/about" className="p5-btn-ghost">WHAT IS DMYOR</Link>
          </div>

          <div className="p5-scroll-hint">SCROLL DOWN ↓</div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="p5-objectives">
        <div className="p5-section-tag">HOW IT WORKS</div>
        <h2 className="p5-section-title">Five steps. Five years.</h2>
        <div className="p5-obj-list">
          {STEPS.map((s) => (
            <div className="p5-obj-row" key={s.num}>
              <div className="p5-obj-num">{s.num}</div>
              <div className="p5-obj-body">
                <div className="p5-obj-label">{s.label}</div>
                <p className="p5-obj-desc">{s.desc}</p>
              </div>
              <div className="p5-obj-bar">
                <div className="p5-obj-fill" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY WRITE ── */}
      <section className="p5-brief">
        <div className="p5-brief-inner">
          <div className="p5-section-tag">WHY IT MATTERS</div>
          <h2 className="p5-section-title">
            You have had this thought
            <span className="p5-green">before</span>
          </h2>
          <div className="p5-brief-body">
            <p>
              A clear picture of the life you want. Who you would be, what you
              would have, what it would feel like. And then life moved on, and
              that picture stayed in your head. Quiet. Unwritten. And slowly, it
              faded. Again.
            </p>
            <p>
              Writing something down changes what it is. It leaves your
              subconscious and enters the real world. It stops being a passing
              thought and becomes a claim. Your future self will read what you
              write here. They will see what you wanted, what scared you, how
              hard you were trying. They will know you showed up.
            </p>
            <p className="p5-brief-highlight">
              Not just for you. Anyone who comes after and wants to understand
              what it was like to be alive right now, wanting something and
              building toward it, will find it here. Don't let five years pass
              without a record.
            </p>
          </div>
        </div>
      </section>

      {/* ── ALL OUT ── */}
      <section className="p5-allout">
        <div className="p5-allout-inner">
          <div className="p5-section-tag">THE CASE</div>
          <h2 className="p5-allout-title">
            Because why not give life<br />
            a <span className="p5-green">serious, all-out effort</span><br />
            to get what you want.
          </h2>
          <p className="p5-allout-body">
            You are going to spend the next five years alive regardless. Working,
            sleeping, scrolling, existing. The time is going whether you use it or
            not. So the only real question is whether you spend it deliberately.
            Committing to what matters to you, moving toward it, and building a
            record of someone who actually tried. The worst case is you tried and
            fell short. The best case is you get exactly the life you designed.
            Both are better than never starting.
          </p>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="p5-features">
        <div className="p5-features-inner">
          <div className="p5-section-tag">WHAT YOU GET</div>
          <h2 className="p5-section-title">Built to keep you on track.</h2>
          <div className="p5-feat-list">
            {FEATURES.map((f) => (
              <div className="p5-feat-row" key={f.num}>
                <div className="p5-feat-num">{f.num}</div>
                <div className="p5-feat-body">
                  <div className="p5-feat-header">
                    <span className="p5-feat-label">{f.label}</span>
                    <span className="p5-feat-tag">{f.tag}</span>
                  </div>
                  <p className="p5-feat-desc">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="p5-cta">
        <div className="p5-cta-inner">
          <div className="p5-system-tag">YOUR CALL</div>
          <h2 className="p5-cta-title">
            Are you in<span className="p5-red">?</span>
          </h2>
          <p className="p5-cta-sub">
            Five years from today, this either happened or it didn't.
            The only variable is what you decide right now.
          </p>
          <Link to="/signup" className="p5-btn-primary p5-btn-large">
            <span className="p5-btn-bracket">[</span>
            START PROJECT-5
            <span className="p5-btn-bracket">]</span>
          </Link>
          <div className="p5-cta-note">
            Already on dmyor? <Link to="/login" className="p5-link">Log in →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectFive;
